import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type TicketType = {
  code: string;
  name: string;
  nameZh: string;
  genderLabel: string;
  priceHkd: number;
  priceLabel: string;
  includes: string;
  includesZh: string;
  bookingChannel: string;
  badge?: string;
  isActive: boolean;
  soldOut?: boolean;
};

type EventListItem = {
  slug: string;
  shortCode: string;
  showShortCode?: boolean;
  title: string;
  titleZh: string;
  heroTitle: string;
  heroSubtitle: string;
  summary: string;
  memberPrivilege: string;
  venue: string;
  address: string;
  organiserContact: string;
  dateLabel: string;
  timeLabel: string;
  startAt: string;
  endAt: string;
  registrationOpen?: boolean;
  ticketTypes: TicketType[];
  photoUrl?: string | null;
};

export default function EventsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [events, setEvents] = useState<EventListItem[]>([]);

  const sortedEvents = useMemo(
    () =>
      [...events].sort((a, b) => {
        const aClosed = a.registrationOpen === false ? 1 : 0;
        const bClosed = b.registrationOpen === false ? 1 : 0;
        return aClosed - bClosed;
      }),
    [events],
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch("/api/events");
        const data = (await res.json()) as { ok?: boolean; rows?: EventListItem[] };
        if (!res.ok || !data.ok || !Array.isArray(data.rows)) {
          throw new Error("failed");
        }
        setEvents(data.rows);
      } catch {
        setError("Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#9B72FF] mb-3">Aurays Events</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4">
              Discover upcoming social events built for real-world connection.
            </h1>
            <p className="text-gray-500 text-base md:text-lg leading-relaxed">
              Browse active events, upload your payment receipt, receive an e-ticket after approval, and check in on
              site with a verified digital pass.
            </p>
          </div>

          {loading ? (
            <div className="bg-white rounded-3xl border border-gray-100 p-8 text-gray-500">Loading events...</div>
          ) : error ? (
            <div className="bg-red-50 rounded-3xl border border-red-100 p-8 text-red-600">{error}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {sortedEvents.map((event) => {
                const isClosed = event.registrationOpen === false;
                return (
                  <div
                    key={event.slug}
                    className={`rounded-3xl border p-7 transition-opacity ${
                      isClosed ? "bg-gray-50 border-gray-200 opacity-60" : "bg-white border-gray-100"
                    }`}
                  >
                    {event.photoUrl ? (
                      <div className="rounded-2xl overflow-hidden mb-4 -mt-2">
                        <img
                          src={event.photoUrl}
                          alt={event.titleZh}
                          className={`w-full aspect-[2/1] object-cover rounded-2xl ${isClosed ? "grayscale-[0.2]" : ""}`}
                        />
                      </div>
                    ) : null}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        {event.showShortCode !== false && event.shortCode ? (
                          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#9B72FF] mb-2">
                            {event.shortCode}
                          </p>
                        ) : null}
                        <h2 className="text-2xl font-bold text-gray-900">{event.titleZh}</h2>
                        <p className="text-sm text-gray-500 mt-1">{event.title}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="bg-[#E6D9FF] text-[#5B3FA0] text-xs font-semibold px-3 py-1.5 rounded-full">
                          {event.dateLabel}
                        </span>
                        {isClosed ? (
                          <span className="bg-gray-200 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                            Registration Closed
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <p className="text-lg font-semibold text-gray-900 mb-2">{event.heroTitle}</p>
                    <p className="text-sm text-gray-500 leading-relaxed mb-5">{event.summary}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Venue</p>
                        <p className="text-sm font-medium text-gray-800">{event.venue}</p>
                      </div>
                      <div className="rounded-2xl bg-gray-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">Time</p>
                        <p className="text-sm font-medium text-gray-800">{event.timeLabel}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {event.ticketTypes.map((ticket) => (
                        <span
                          key={ticket.code}
                          className={`inline-flex items-center rounded-full text-xs font-medium px-3 py-1.5 ${ticket.soldOut ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-700"}`}
                        >
                          {ticket.nameZh} · {ticket.priceLabel}
                          {ticket.soldOut ? " · Sold Out" : ""}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/events/${encodeURIComponent(event.slug)}`}
                      className={`inline-flex items-center gap-2 font-semibold px-5 py-3 rounded-full transition-colors ${
                        isClosed
                          ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          : "bg-gray-900 text-white hover:bg-gray-700"
                      }`}
                    >
                      {isClosed ? "View Details" : "View Event"}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
